import { Box, Stack, Typography } from '@mui/material'

import { layout } from 'src/theme/tokens.generated'
import {
  MAP_EDGE_INSET,
  MAP_FOOT_INSET,
  MAP_LAYER,
  MAP_RECENTRE_ROOM,
} from './consts'
import { MARKER_STATES } from './markerState'

// ----------------------------------------------------------------------
// What the marker tints mean.
//
// The map colours a vehicle by what it is doing, and a colour is only a
// signal to someone who knows what it stands for. Every marker also carries
// a glyph, so the legend is a convenience rather than the only key, but a
// fleet map without one asks an operator to learn it by inference.
//
// **The swatch is the marker's own dot**, read from the same table the marker
// draws from and drawn at the same size, `layout.mapMarkerDot`. It used to be
// a second list that agreed with the marker on the words and not on the
// colour: the marker painted the categorical `bg` and the key painted the
// categorical `text`, so the legend showed four dark inks for four near-white
// pins. A key that is not the colour on the map has to be distrusted before it
// can be used, which is worse than having none. The size is the other half of
// that: the marker carried a 24px glyph badge where the key drew a 10px plain
// dot, so the two were the same colour in two different marks and the key
// still had to be translated. One mark, one size, one table.
// ----------------------------------------------------------------------

export default function MapLegend() {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={(theme) => ({
        position: 'absolute',
        zIndex: MAP_LAYER.furniture,
        bottom: theme.spacing(MAP_FOOT_INSET),
        left: theme.spacing(MAP_EDGE_INSET),
        // Never under the recentre target: on a 360px phone the legend ran
        // 24px beneath it and `Disabled` was cut short.
        maxWidth: `calc(100% - ${theme.spacing(
          MAP_EDGE_INSET * 2 + MAP_RECENTRE_ROOM,
        )})`,
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: theme.radii.pill,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: theme.customShadows.dropdown,
        flexWrap: 'wrap',
        // On a phone a key of two columns on a card, not a pill: the four
        // states wrapped three and one under the pill's round ends (asked
        // on 2026-10-31: "doesnt look good, whats a better solution for
        // mobile?").
        [theme.breakpoints.down('sm')]: {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, max-content)',
          columnGap: 2,
          rowGap: 0.75,
          py: 1.25,
          borderRadius: theme.radii.surface,
        },
      })}
    >
      {MARKER_STATES.map(({ state, label, tint }) => (
        <Stack
          key={state}
          direction="row"
          alignItems="center"
          sx={{ gap: 0.75 }}
        >
          <Box
            data-testid={`legend-swatch-${state}`}
            sx={{
              width: layout.mapMarkerDot,
              height: layout.mapMarkerDot,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: (theme) => theme.palette.vivid[tint],
            }}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}
